import { create, StoreApi, UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { ISlot } from '../organisms/bo_calendar_slot/types';
import { calculateOverallSlotTimes, getSlotOverlapDates } from './utils';
import { IResource } from './types';

export type CalendarStore = {
	slots: Record<number, ISlot[]>;
	resources: IResource[];
	updateResourceSlot: (resourceId: number, slot: ISlot) => void;
	getSlotsByResourceId: (resourceId: number) => ISlot[];
	getResourceSlotsByDate: (resourceId: number) => Map<string, ISlot[]>;
};

export const createCalendarStore = ({
	slots,
	resources,
}: Pick<CalendarStore, 'slots' | 'resources'>): UseBoundStore<StoreApi<CalendarStore>> => {
	return create(
		subscribeWithSelector((set, get) => ({
			slots,
			resources,
			updateResourceSlot: (resourceId: number, slot: ISlot) => {
				const { slots }: CalendarStore = get();

				if (Number(slot.resourceId) !== resourceId) {
					const previousResourceSlots: ISlot[] = slots[resourceId].filter(s => s.id !== slot.id);
					const newResourceSlots: ISlot[] = [...(slots[slot.resourceId] ?? []), slot];

					return set({
						slots: {
							...slots,
							[resourceId]: previousResourceSlots,
							[slot.resourceId]: newResourceSlots,
						},
					});
				}

				const updatedResourceSlots: ISlot[] = Array.from(slots[resourceId]);
				const index: number = updatedResourceSlots.findIndex(s => s.id === slot.id);

				updatedResourceSlots[index] = slot;

				return set({
					slots: {
						...slots,
						[resourceId]: updatedResourceSlots,
					},
				});
			},
			getSlotsByResourceId: (resourceId: number) => {
				const { slots }: CalendarStore = get();
				return slots?.[resourceId] ?? [];
			},
			getResourceSlotsByDate: (resourceId: number): Map<string, ISlot[]> => {
				const { slots }: CalendarStore = get();
				const resourceSlots: ISlot[] = slots?.[resourceId] || [];
				const slotsByDate: Map<string, ISlot[]> = new Map();

				if (!resourceSlots.length) {
					return slotsByDate;
				}

				for (const slot of resourceSlots) {
					const dates: string[] = getSlotOverlapDates(slot);

					for (const date of dates) {
						// cloning the slot to avoid mutating the original data
						const clonedSlot: ISlot = structuredClone(slot);

						if (!slotsByDate.has(date)) {
							slotsByDate.set(date, [calculateOverallSlotTimes(clonedSlot, date)]);
							continue;
						}

						slotsByDate.get(date)!.push(calculateOverallSlotTimes(clonedSlot, date));
					}
				}

				return slotsByDate;
			},
		}))
	);
};
